import { useMemo } from "react";
import { useUsersList } from "../../hooks/useUsersList.js";
import { getUserTypeConfig, getUserTypeLabel, USER_TYPES } from "../../utils";
import { FILTER_STATUS } from "../../utils";
import { Icons } from "../../utils/icons.jsx";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal.jsx";
import DomainBadge from "../common/DomainBadge.jsx";
import Modal from "../common/Modal.jsx";
import UserCard from "./UserCard.jsx";
import UserForm from "./UserForm.jsx";

function UsersList() {
  const {
    users,
    filteredUsers,
    loading,
    error,
    successMessage,

    modalState,
    userTypeFilter,
    includeInactive,
    toggleIncludeInactive,
    setUserTypeFilter,

    handleCreate,
    handleEdit,
    handleViewDetails,
    handleDeleteClick,
    handleDelete,
    handleFormSubmit,
    handleSuccess,

    closeUserModal,
    closeDeleteModal,
    closeUserCard,
    clearSuccessMessage,
    clearError,
  } = useUsersList();

  // --- Filter options dynamically ---
  const filterOptions = [FILTER_STATUS.ALL, ...Object.values(USER_TYPES)];

  // --- Table row click ---
  const handleRowClick = (e, userId) => {
    if (e.target.closest(".table-actions") || e.target.closest("button"))
      return;
    handleViewDetails(userId);
  };

  if (loading) {
    return (
      <div className="loading">
        <Icons.Loading className="spin" /> Chargement ...
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="list-header">
        <h2>Liste des membres</h2>
        <div className="list-header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            <Icons.Add /> Ajouter
          </button>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="list-filters">
        <div className="filter-pills">
          {filterOptions.map((type) => (
            <button
              key={type}
              type="button"
              className={`pill ${userTypeFilter === type ? "pill-active" : ""}`}
              onClick={() => setUserTypeFilter(type)}
            >
              {type === FILTER_STATUS.ALL ? "Tous" : getUserTypeLabel(type)}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="list-messages">
        {error && (
          <div className="alert alert-error">
            <Icons.Warning /> {error}
            <button className="btn btn-sm btn-secondary" onClick={clearError}>
              Effacer
            </button>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <Icons.Check /> {successMessage}
            <button
              className="btn btn-sm btn-secondary"
              onClick={clearSuccessMessage}
            >
              OK
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Statut</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>-</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const userTypeConfig = getUserTypeConfig(user.user_type);
              return (
                <tr
                  key={user.id}
                  className="row-clickable"
                  onClick={(e) => handleRowClick(e, user.id)}
                >
                  <td>
                    <strong>{user.last_name}</strong>
                  </td>
                  <td>
                    <strong>{user.first_name}</strong>
                  </td>
                  <td>
                    {userTypeConfig && <DomainBadge config={userTypeConfig} />}
                  </td>
                  <td>{user.email || "-"}</td>
                  <td>{user.phone || "-"}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className="btn-icon-modern"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(user.id);
                      }}
                    >
                      <Icons.View />
                    </button>
                  </td>
                </tr>
              );
            })}

            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-state-small">
                  Aucun membre correspondant au filtre.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* User Form Modal */}
      <Modal
        isOpen={modalState.showUserModal}
        onClose={closeUserModal}
        size="md"
      >
        <UserForm
          user={modalState.editingUser}
          onSubmit={handleFormSubmit}
          onCancel={closeUserModal}
        />
      </Modal>

      {/* User Details Card */}
      {modalState.selectedUserId && (
        <UserCard
          userId={modalState.selectedUserId}
          onClose={closeUserCard}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onSuccess={handleSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={modalState.showDeleteModal}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        itemType="user"
        itemName={modalState.userToDelete?.name}
      />
    </div>
  );
}

export default UsersList;
